// src/components/MilestoneShelf.js
//
// The member's badge shelf on Home. Purely presentational — every value comes
// from computeMilestones(), which derives from completions already in memory.
// No fetching, no writes.
//
// Renders earned badges oldest-first (the shelf reads as a journey), then the
// single closest unearned badge with real progress — so it always answers
// "what's next?" without turning into a grid of things you haven't done.

import React, { useState } from 'react';
import MilestoneBadge from './MilestoneBadge';
import MilestoneDetail, { badgeButtonReset } from './MilestoneDetail';
import { BADGES, bestRunNote } from '../services/milestones';

// Tapping any badge opens its detail card; the card browses the whole
// collection in catalogue order (earned or not). A seasonal badge is included
// only while it's visible to this member (computeMilestones().visibleKeys).
const ALL_KEYS = BADGES.filter((b) => b.family !== 'seasonal').map((b) => b.key);

const MAX_VISIBLE = 8;

function formatEarned(dayKey) {
    if (!dayKey) return '';
    const [y, m, d] = dayKey.split('-').map(Number);
    if (!y || !m || !d) return '';
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    const now = new Date();
    const sameYear = dt.getUTCFullYear() === now.getFullYear();
    return dt.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
        timeZone: 'UTC',
    });
}

function MilestoneShelf({ milestones, copy = {}, onViewAll }) {
    const [hovered, setHovered] = useState(null);
    const [openKey, setOpenKey] = useState(null);
    if (!milestones) return null;

    const { earned = [], next = null, classCount = 0 } = milestones;

    // Nothing earned and nothing in progress ⇒ render nothing rather than an
    // empty trophy case. A member with zero classes gets the existing Home
    // exactly as it is today.
    if (earned.length === 0 && classCount === 0) return null;

    const visible = earned.slice(-MAX_VISIBLE);
    const heading = copy.milestonesHeading || 'Your Class Milestones';
    const countLabel = `${earned.length} earned · ${classCount} ${classCount === 1 ? 'class' : 'classes'} completed`;

    return (
        <div
            className="mom-card"
            style={{
                // No `background` here on purpose: .mom-card supplies the white
                // gradient + specular treatment every other card uses. Setting
                // --mom-card-bg would pick up the admin's "Content Card" colour
                // (a dusty rose), which is meant for video/program tiles.
                borderRadius: 26,
                padding: '26px 28px',
                marginBottom: 20,
                boxShadow: 'var(--shadow-md, 0 10px 30px rgba(45,41,38,.07))',
                animation: 'fadeUp 0.5s ease both',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
                <h3 style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 21,
                    fontWeight: 600, color: 'var(--mom-charcoal)', margin: 0,
                }}>
                    {heading}
                </h3>
                {onViewAll && (
                    <button
                        onClick={onViewAll}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600,
                            color: 'var(--mom-link, var(--mom-primary))',
                        }}
                    >
                        {copy.milestonesViewAllLabel || 'View all'} →
                    </button>
                )}
            </div>

            <p style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                color: 'var(--mom-warm-gray)', margin: '4px 0 20px',
            }}>
                {countLabel}
            </p>

            <div style={{ display: 'flex', gap: 20, overflowX: 'auto', padding: '4px 2px 8px' }}>
                {visible.map((b) => (
                    <button
                        type="button"
                        key={b.key}
                        aria-label={`${b.name} — how it's earned`}
                        onClick={() => setOpenKey(b.key)}
                        onMouseEnter={() => setHovered(b.key)}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                            ...badgeButtonReset,
                            flex: '0 0 auto', width: 96, textAlign: 'center',
                            transform: hovered === b.key ? 'translateY(-2px)' : 'none',
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <MilestoneBadge badgeKey={b.key} size={80} />
                        <div style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                            color: 'var(--mom-charcoal)', marginTop: 9, lineHeight: 1.35,
                        }}>
                            {b.name}
                        </div>
                        <div style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 10.5,
                            color: 'var(--mom-warm-gray)', marginTop: 2,
                        }}>
                            {formatEarned(b.earnedDayCT)}
                        </div>
                    </button>
                ))}

                {next && (
                    <button
                        type="button"
                        aria-label={`${next.name} — how it's earned`}
                        onClick={() => setOpenKey(next.key)}
                        onMouseEnter={() => setHovered(next.key)}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                            ...badgeButtonReset,
                            flex: '0 0 auto', width: 96, textAlign: 'center', opacity: 0.95,
                            transform: hovered === next.key ? 'translateY(-2px)' : 'none',
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <MilestoneBadge badgeKey={next.key} size={80} locked progress={next.progress} />
                        <div style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                            color: 'var(--mom-warm-gray)', marginTop: 9, lineHeight: 1.35,
                        }}>
                            {next.name}
                        </div>
                        <div style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 10.5,
                            color: 'var(--mom-warm-gray)', marginTop: 2,
                        }}>
                            {next.current} of {next.target}
                        </div>
                    </button>
                )}
            </div>

            <TapHint copy={copy} />

            {next && (
                <div style={{
                    marginTop: 18, paddingTop: 18,
                    borderTop: '1px solid var(--mom-cream-dark)',
                    display: 'flex', alignItems: 'center', gap: 14,
                }}>
                    <div style={{
                        flex: 1, height: 6, background: 'var(--mom-cream-dark)',
                        borderRadius: 99, overflow: 'hidden',
                    }}>
                        <div style={{
                            height: '100%', width: `${Math.round(next.progress * 100)}%`,
                            background: 'linear-gradient(90deg, var(--mom-primary), var(--mom-primary-light))',
                            borderRadius: 99, transition: 'width 0.6s ease',
                        }} />
                    </div>
                    <div style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                        color: 'var(--mom-charcoal-light, #5c5651)', whiteSpace: 'nowrap',
                    }}>
                        <b style={{ color: 'var(--mom-charcoal)' }}>
                            {Math.max(0, next.target - next.current)} more
                        </b>{' '}
                        to {next.name}
                        {/* Weekly badges track the CURRENT run; an earlier
                            longer run is acknowledged here rather than lost. */}
                        {bestRunNote(next, copy) && (
                            <span style={{ color: 'var(--mom-warm-gray)' }}>
                                {' · '}{bestRunNote(next, copy)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {openKey && (
                <MilestoneDetail
                    badgeKey={openKey}
                    keys={milestones.visibleKeys || ALL_KEYS}
                    milestones={milestones}
                    copy={copy}
                    onNavigate={setOpenKey}
                    onClose={() => setOpenKey(null)}
                />
            )}
        </div>
    );
}

// Quiet discoverability line: tells members the badges open.
export function TapHint({ copy = {}, style }) {
    const text = (typeof copy.milestoneTapHint === 'string' && copy.milestoneTapHint.trim())
        ? copy.milestoneTapHint : "Tap a badge to see how it's earned";
    return (
        <p style={{
            display: 'flex', alignItems: 'center', gap: 6, margin: '10px 0 0',
            fontFamily: 'var(--mom-font-sans)', fontSize: 11.5,
            color: 'color-mix(in srgb, var(--mom-charcoal) 50%, var(--mom-cream))',
            ...style,
        }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><line x1="12" y1="11" x2="12" y2="16.5" /><circle cx="12" cy="7.6" r="0.6" fill="currentColor" /></svg>
            {text}
        </p>
    );
}

export default MilestoneShelf;
